/**
 * The War Room. The native coordination surface, and the reason Operations exist at all.
 *
 * GAME-DESIGN 8.6: "Torn's deepest skill (defence coordination, sniping, backtiming) lives in
 * third-party spreadsheets at 3am. Ashport's equivalent depth must live INSIDE this screen and
 * inside the window, or it will not exist for anyone who is not already in a Discord."
 *
 * **THIS IS NOT THE FILE THAT WAS DELETED AT 1k.11.** The mockup at `19ba027` was 1,056 lines and
 * every number in it was invented. Its LAYOUT is lifted here, which is what 1j.11 asked for, and
 * its three scoring rules are not:
 *
 *   - **A `SQUADS` array of six**, citing a unit that appears zero times in GAME-DESIGN. 1m.5
 *     deleted the squad outright: the unit of in-window coordination is the OPERATION.
 *   - **An overlap schedule with prep, overlap and cool bands and a 2.0x points multiplier**,
 *     which is the mutual-overlap nomination rule 1j.11 overturned. Four public windows on the
 *     clock need no schedule at all.
 *   - **A hit-based scoreboard**, replaced at 1j.11 by capped occupation and specified at 1n.1:
 *     presence is a slot, five a side per block, and the score is the Nerve committed through it.
 *
 * It also carried five invented intel products (1m.9 rules two), six sabotage missions whose
 * district labels did not match their own artwork, and four canon names typed as literals, which
 * `tools/content/validate.mjs` section 8 now refuses outright.
 *
 * WHAT IS NOT BUILT SAYS SO. Sabotage, espionage and stockpiling are the tail of stage E and they
 * are named below with where they live, rather than drawn as a working panel over nothing. That is
 * 1l.3, and `Territory.tsx` is the worked example of it twice over.
 */

import { useEffect, useState } from 'react'
import {
  OPERATION_COMMIT_NERVE,
  OPERATION_LAST_CALL_MS,
  PRESENCE_CAP_PER_SIDE,
} from '@ashport/shared/formulas'
import { Icon } from '../ui/Icon'
import { Chip, Col, Panel, PanelHead, Rail, Row, Stack, Stat } from '../ui/Kit'
import { useViewport } from '../ui/viewport'
import { useGame } from '../game/state'
import { useSession } from '../game/session'

const PHASE_LINE: Record<string, string> = {
  quiet: 'No window. Nothing here is actionable and nothing needs to be.',
  declared: 'Called out. The doors can still be worked.',
  mustering: 'Inside the day. Rosters settle, nothing lands yet.',
  active: 'Open. Everything on this screen is live for two hours.',
  aftermath: 'Closed. The board is what it is.',
  closed: 'Over.',
}

/** Every block anybody has a live quarrel over, plus your own ground. What is worth opening. */
function Picker({ onPick, pick }: { onPick: (id: string) => void; pick: string | null }) {
  const t = useSession((s) => s.territory)
  const blocks = t?.blocks ?? []
  const wars = t?.wars ?? []
  const ids = new Set([
    ...wars.map((w) => w.blockId),
    ...blocks.filter((b) => b.owner === 'you' || b.owner === 'contested').map((b) => b.blockId),
  ])
  const rows = blocks.filter((b) => ids.has(b.blockId))

  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead title="Where the fight is" icon="target" sub={`${rows.length} blocks`} />
      <div className="scroll-y" style={{ maxHeight: 220 }}>
        {rows.length === 0 ? (
          <p style={{ margin: 0, padding: '12px 14px', fontSize: 11.5, color: 'var(--ink-dim)' }}>
            Nobody has called anybody out and you hold no ground. The map is on Territory.
          </p>
        ) : (
          <Col gap={0}>
            {rows.map((b) => (
              <div key={b.blockId} onClick={() => onPick(b.blockId)} style={{ cursor: 'pointer' }}>
                <Row
                  style={{
                    justifyContent: 'space-between',
                    padding: '7px 12px',
                    background: b.blockId === pick ? 'rgba(255,255,255,.05)' : undefined,
                  }}
                >
                  <span style={{ fontSize: 11.5, color: 'var(--ink-hi)' }}>{b.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--ink-dim)' }}>
                    {b.control} / {b.fortTierName}
                  </span>
                </Row>
              </div>
            ))}
          </Col>
        )}
      </div>
    </Panel>
  )
}

/**
 * The hero band: who is fighting, over what, and what the two boards read.
 *
 * The mockup's version of this panel showed a projected score over an invented overlap window.
 * This one shows the two numbers the server actually compares and the threshold between them,
 * which is 8.4's flip rule made visible instead of implied.
 */
function Band() {
  const board = useSession((s) => s.warBoard)
  const { now } = useGame()
  if (board === null) return null

  return (
    <Panel tone="danger" style={{ flex: 'none' }}>
      <PanelHead
        title={board.blockName}
        icon="target"
        accent="var(--heat)"
        sub={board.windowLabel === '' ? 'no window' : board.windowLabel}
        right={<Chip c="var(--heat)">{board.phase}</Chip>}
      />
      <Col gap={9} style={{ padding: '10px 14px 14px' }}>
        <Row style={{ justifyContent: 'space-between' }}>
          <Stat label="Your board" value={board.yourScore} c="var(--money)" />
          <Stat label="To take it" value={board.flipAt} c="var(--gold)" align="center" />
          <Stat label="Theirs" value={board.holderScore} c="var(--heat)" align="right" />
        </Row>
        <Row style={{ justifyContent: 'space-between' }}>
          <Stat label="Control" value={board.control} sub="capture at zero" />
          <Stat
            label="Your slots"
            value={`${board.slotsUsed} of ${board.slotsCap}`}
            align="right"
            sub={board.youHoldASlot ? 'you are on it' : 'you are not on it'}
          />
        </Row>
        <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>
          {PHASE_LINE[board.phase] ?? ''}
        </p>
        <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-dim)', lineHeight: 1.5 }}>
          Control at zero opens the door. The board decides whether you walk through it: a tie does
          not take ground, and {PRESENCE_CAP_PER_SIDE} of a crew is the most that fits on one block.
          The clock reads {new Date(now).toUTCString().slice(17, 22)} UTC.
        </p>
      </Col>
    </Panel>
  )
}

/**
 * OPERATIONS. One tap, ninety seconds, and the tap counts the moment it is made.
 *
 * 8.6 calls this "the most important retention lever in the whole war layer" and the reason is the
 * sentence under the button: a commit scores at commit time, not at resolution, so a member whose
 * Capo goes to bed has still contributed and still appears on the board. DESIGN-DELTAS 1n.6.
 */
function Operations() {
  const board = useSession((s) => s.warBoard)
  const busy = useSession((s) => s.busy)
  const last = useSession((s) => s.lastTerritory)
  const refusal = useSession((s) => s.refusal)
  const call = useSession((s) => s.callOperation)
  const join = useSession((s) => s.joinOperation)
  const trigger = useSession((s) => s.triggerOperation)
  const { now } = useGame()
  if (board === null) return null
  const live = board.phase === 'active'

  return (
    <Panel tone="gold" style={{ flex: 'none' }}>
      <PanelHead
        title="Operations"
        icon="families"
        accent="var(--gold)"
        sub={`${OPERATION_COMMIT_NERVE} Nerve, one tap`}
      />
      <Col gap={7} style={{ padding: '10px 12px 12px' }}>
        {board.operations.length === 0 && (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-dim)', lineHeight: 1.5 }}>
            Nobody has called one. A Capo opens it and everybody else puts one tap in.
          </p>
        )}
        {board.operations.map((op) => {
          const lastCall = now >= op.expiresAt - OPERATION_LAST_CALL_MS
          return (
            <Col key={op.opId} gap={5}>
              <Row style={{ justifyContent: 'space-between' }}>
                {/* The leader's NAME comes from the server, which read it off the roster. */}
                <span style={{ fontSize: 11.5, color: 'var(--ink-hi)' }}>{op.leaderName}</span>
                <span style={{ fontSize: 11, color: 'var(--ink-dim)' }}>
                  {op.contributors} in, {op.nervePooled} Nerve
                </span>
              </Row>
              {op.state === 'open' && !op.youAreIn && (
                <button className="btn" disabled={busy || !live} onClick={() => void join(op.opId)}>
                  Put your hand up for {OPERATION_COMMIT_NERVE} Nerve
                </button>
              )}
              {op.state === 'open' && op.youAreIn && (
                <button
                  className="btn"
                  disabled={busy || !live}
                  onClick={() => void trigger(op.opId)}
                >
                  {lastCall ? 'Last call. Take them in.' : 'Take them in'}
                </button>
              )}
              {op.state === 'resolved' && (
                <span style={{ fontSize: 11, color: 'var(--ink-mid)' }}>
                  Went in {op.contributors} strong and knocked {op.knocked} off.
                </span>
              )}
            </Col>
          )
        })}
        <button className="btn" disabled={busy || !live} onClick={() => void call(board.blockId)}>
          Call an Operation
        </button>
        <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-dim)', lineHeight: 1.5 }}>
          Your tap goes on the board the moment you make it, whether or not anybody ever calls it
          in. In the last ten minutes of the window, anybody who paid in can take them in.
        </p>
        {refusal !== null && (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--heat)' }}>{refusal}</p>
        )}
        {refusal === null && last !== null && (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--money)' }}>{last}</p>
        )}
      </Col>
    </Panel>
  )
}

/** Who has committed what, in commit order. The thing 8.6 says must be live on this screen. */
function Scoreboard() {
  const board = useSession((s) => s.warBoard)
  if (board === null) return null
  return (
    <Panel style={{ flex: 1, minHeight: 0 }}>
      <PanelHead
        title="Who is on it"
        icon="trophy"
        sub={`${board.scoreboard.length} committed`}
        accent="var(--gold)"
      />
      <div className="scroll-y" style={{ flex: 1 }}>
        {board.scoreboard.length === 0 ? (
          <p style={{ margin: 0, padding: '12px 14px', fontSize: 11.5, color: 'var(--ink-dim)' }}>
            Nobody has swung yet. The first person on a block takes the first of five slots.
          </p>
        ) : (
          <table className="grid-tbl">
            <thead>
              <tr>
                <th>Crew</th>
                <th style={{ width: 90 }}>On the board</th>
              </tr>
            </thead>
            <tbody>
              {board.scoreboard.map((r) => (
                <tr key={`${r.familyId}:${r.playerId}`}>
                  <td style={{ color: 'var(--ink-mid)' }}>{r.familyName || 'A crew'}</td>
                  <td style={{ color: 'var(--ink-hi)' }}>{r.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Panel>
  )
}

/**
 * What this screen does not do yet, named rather than drawn.
 *
 * The mockup filled this column with sabotage missions, an espionage board and five stockpile
 * meters, none of which had a server. 1l.3 rules that a screen for a system with no server renders
 * its authored content and says so.
 */
function NotYet() {
  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead title="Not yet" icon="lock" sub="named, not drawn" />
      <Col gap={6} style={{ padding: '10px 12px 12px' }}>
        <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>
          Sabotage is ten effects, each of them stated in the design and none of them written up
          yet. Espionage is two products and they land on a window boundary, never on a stopwatch.
          Stockpiling is five categories bought with clean cash.
        </p>
        <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-dim)', lineHeight: 1.5 }}>
          None of the three has a server behind it, so none of the three is drawn here. A panel of
          invented numbers is worse than a paragraph that tells you the truth.
        </p>
      </Col>
    </Panel>
  )
}

export function WarRoom() {
  const t = useSession((s) => s.territory)
  const loadTerritory = useSession((s) => s.loadTerritory)
  const loadBoard = useSession((s) => s.loadWarBoard)
  const board = useSession((s) => s.warBoard)
  const [pick, setPick] = useState<string | null>(null)

  useEffect(() => {
    void loadTerritory().catch(() => undefined)
  }, [loadTerritory])

  useEffect(() => {
    if (pick !== null) void loadBoard(pick).catch(() => undefined)
  }, [pick, loadBoard])
  const { stacked } = useViewport()

  return (
    <Stack>
      <Col gap={8} style={stacked ? { flex: 'none', minWidth: 0 } : { flex: 1, minWidth: 0 }}>
        <Row gap={9} style={{ flex: 'none', flexWrap: 'wrap' }}>
          <span className="t-display" style={{ fontSize: 30, color: 'var(--ink-hi)' }}>
            War Room
          </span>
          <span style={{ fontSize: 12, color: 'var(--ink-mid)', fontStyle: 'italic' }}>
            Two hours, four times a week. Everything that matters happens in here.
          </span>
          <div style={{ flex: 1 }} />
          {t?.openWindow !== null && t !== null && <Chip c="var(--heat)">A window is open</Chip>}
        </Row>
        {/* Stacked, the picker sits under the title, because it is the first thing to press. */}
        {stacked && <Picker onPick={setPick} pick={pick} />}
        {board === null ? (
          <Panel style={stacked ? { flex: 'none' } : { flex: 1 }}>
            <PanelHead title="Pick a block" icon="target" />
            <div style={{ padding: '22px 18px', textAlign: 'center' }}>
              <Icon n="target" s={22} style={{ opacity: 0.4 }} />
              <p style={{ margin: '8px 0 0', fontSize: 11.5, color: 'var(--ink-mid)' }}>
                {stacked
                  ? 'Choose a street above and this becomes the board for it.'
                  : 'Choose a street on the right and this becomes the board for it.'}
              </p>
            </div>
          </Panel>
        ) : (
          <>
            <Band />
            <Scoreboard />
          </>
        )}
      </Col>

      <Rail width={322}>
        {!stacked && <Picker onPick={setPick} pick={pick} />}
        {board !== null && <Operations />}
        <NotYet />
      </Rail>
    </Stack>
  )
}
